// src/pages/gene.codes/bloods/BloodAnalysiscomponents/BloodAnalysisSidebar.tsx
import React, { useState, useEffect } from "react";
import { Collapse, Tag, Badge, Button, Typography } from "antd";
import {
	HeartOutlined,
	LineChartOutlined,
	AppleOutlined,
	ExclamationCircleOutlined,
	BulbOutlined,
	BarChartOutlined,
	CalendarOutlined,
	UserOutlined,
	ClockCircleOutlined,
	EyeOutlined,
	SearchOutlined,
	BookOutlined,
	InfoCircleOutlined,
	SafetyCertificateOutlined,
	CloseOutlined,
} from "@ant-design/icons";

import { SectionCard, StatusTag } from "../../../../components/shared";




import ResultRangeBar from "./ResultRangeBar";
import HistoricalTrendChart from "./HistoricalTrendChart";
import { labWallClockDate } from "../../../../utils/geneCodes/bloodTestShape";
import BloodAnalysisStateNotice from "./BloodAnalysisStateNotice";

import { BloodTest, BloodTestResult, BloodTestAnalysis } from "../../../../types/lab/BloodTestTypes";
import { Test } from "../../../../types/healthcare/TestTypes";
import { calculateResultSeverity } from "../../../../utils/geneCodes/bloodResultUtils";
import { RangeContext } from "../../../../utils/geneCodes/referenceRange";

const { Text } = Typography;

// Health-analysis sections are a fixed set tied to the BloodTestAnalysis schema
// fields (one narrative per body system). These are NOT the blood-test wheel
// grouping categories — the AI populates exactly these keys.
const HEALTH_ANALYSIS_CATEGORIES: { key: string; title: string }[] = [
	{ key: "cardiovascular_analysis", title: "Cardiovascular" },
	{ key: "metabolic_analysis", title: "Metabolic" },
	{ key: "kidney_function_analysis", title: "Kidney Function" },
	{ key: "liver_function_analysis", title: "Liver Function" },
	{ key: "nutritional_status_analysis", title: "Nutritional Status" },
	{ key: "inflammation_analysis", title: "Inflammation" },
	{ key: "hormonal_analysis", title: "Hormonal" },
];

interface BloodAnalysisSidebarProps {
	analysis: BloodTestAnalysis | null;
	bloodTest: BloodTest | null;
	selectedCategory: string | null;
	selectedResult: BloodTestResult | null;
	setSeletecedResult: React.Dispatch<React.SetStateAction<BloodTestResult | null>>;
	viewMode: string;
	allTests: Test[];
	rangeContext: RangeContext;
}

const BloodAnalysisSidebar: React.FC<BloodAnalysisSidebarProps> = ({ analysis, bloodTest, selectedCategory, selectedResult, setSeletecedResult, viewMode, allTests, rangeContext }) => {
	const [activeKeys, setActiveKeys] = useState<string[]>(["1"]);
	const [isMobile, setIsMobile] = useState(false);
	const [chartWidth, setChartWidth] = useState(328);

	useEffect(() => {
		const checkMobile = () => {
			const mobile = window.innerWidth < 768;
			setIsMobile(mobile);
			setChartWidth(mobile ? window.innerWidth - 80 : 328);
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);

		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	const getPriorityStatus = (priority: string) => {
		switch (priority?.toLowerCase()) {
			case "urgent": return "failed";
			case "high": return "waiting";
			case "moderate": return "info";
			case "low": return "completed";
			default: return "info";
		}
	};

	const getRelatedResults = (selectedResult: BloodTestResult) => {
		if (!bloodTest?.results || !selectedResult.test_category) return [];

		return bloodTest.results.filter((r) => r.test_category === selectedResult.test_category && r.id !== selectedResult.id).slice(0, 4);
	};

	const renderNoAnalysis = () => (
		<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", padding: 24 }}>
			<BloodAnalysisStateNotice analysis={analysis} />
		</div>
	);

	const renderSelectedResultDetails = () => {
		if (!selectedResult) return null;

		const relatedResults = getRelatedResults(selectedResult);

		return (
			<div>
				<SectionCard
					title="Selected Test Details"
					extra={<Button icon={<CloseOutlined />} onClick={() => setSeletecedResult(null)} type="text" size="small" />}
					style={{ marginBottom: 8 }}
				>

					<div style={{ marginBottom: "16px" }}>
						<ResultRangeBar result={selectedResult} rangeContext={rangeContext} compact={false} showLabels={true} />
					</div>

					{allTests && allTests.length > 0 && (
						<div style={{ marginBottom: "16px" }}>
							<HistoricalTrendChart selectedResult={selectedResult} allTests={allTests} rangeContext={rangeContext} width={chartWidth} height={isMobile ? 180 : 200} compact={false} />
						</div>
					)}

					<div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: "12px", fontSize: "11px" }}>
						{selectedResult.definition_description && (
							<div style={{ gridColumn: isMobile ? "1" : "span 2" }}>
								<div style={{ marginBottom: 8 }}>
									<Text type="secondary" style={{ fontSize: 11, fontWeight: 500, display: "block", marginBottom: 4 }}>ABOUT THIS TEST</Text>
									<div style={{ fontSize: 11, padding: 8, borderRadius: 4, border: "1px solid var(--mg-border)" }}>{selectedResult.definition_description}</div>
								</div>
							</div>
						)}

						{selectedResult.definition_clinical_use && (
							<div style={{ gridColumn: isMobile ? "1" : "span 2" }}>
								<div style={{ marginBottom: 8 }}>
									<Text type="secondary" style={{ fontSize: 11, fontWeight: 500, display: "block", marginBottom: 4 }}>CLINICAL USE</Text>
									<div style={{ fontSize: 11, padding: 8, borderRadius: 4, border: "1px solid var(--mg-border)" }}>{selectedResult.definition_clinical_use}</div>
								</div>
							</div>
						)}

						{selectedResult.method && (
							<div style={{ gridColumn: isMobile ? "1" : "span 2" }}>
								<div style={{ marginBottom: "8px" }}>
									<span style={{ fontSize: "11px", fontWeight: 500, display: "block", marginBottom: "4px" }}>METHOD</span>
									<span style={{ fontWeight: 500 }}>{selectedResult.method}</span>
								</div>
							</div>
						)}

						{selectedResult.comments && (
						<div style={{ gridColumn: isMobile ? "1" : "span 2" }}>
							<div style={{ marginBottom: 8 }}>
								<Text type="secondary" style={{ fontSize: 11, fontWeight: 500, display: "block", marginBottom: 4 }}>COMMENTS</Text>
								<div style={{ fontSize: 11, padding: 8, borderRadius: 4, border: "1px solid var(--mg-border)" }}>{selectedResult.comments}</div>
							</div>
						</div>
					)}

					{selectedResult.interpretation && (
						<div style={{ gridColumn: isMobile ? "1" : "span 2" }}>
							<div style={{ marginBottom: 8 }}>
								<Text type="secondary" style={{ fontSize: 11, fontWeight: 500, display: "block", marginBottom: 4 }}>INTERPRETATION</Text>
								<div style={{ fontSize: 11, padding: 8, borderRadius: 4, border: "1px solid var(--mg-border)" }}>{selectedResult.interpretation}</div>
							</div>
						</div>
					)}

					<div>
						<Text type="secondary" style={{ fontSize: 11, fontWeight: 500, display: "block", marginBottom: 4 }}>REPORTED</Text>
						<Text style={{ fontSize: 11 }}>{labWallClockDate(selectedResult.collected_at || selectedResult.reported_at)?.toLocaleString() ?? "Not specified"}</Text>
					</div>

					<div>
						<Text type="secondary" style={{ fontSize: 11, fontWeight: 500, display: "block", marginBottom: 4 }}>PATHOLOGIST</Text>
						<Text style={{ fontSize: 11 }}>{selectedResult.supervising_pathologist || "Not specified"}</Text>
					</div>
				</div>
			</SectionCard>
				{relatedResults.length > 0 && (
			<SectionCard title="Related Tests" style={{ marginBottom: 8 }}>
				<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					{relatedResults.map((result) => (
						<div key={result.id} style={{ padding: 8, border: "1px solid var(--mg-border)", borderRadius: 4 }}>
									<ResultRangeBar result={result} rangeContext={rangeContext} compact={true} showLabels={false} />
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
										<Text type="secondary" style={{ fontSize: 11, fontWeight: 500, display: "block" }}>{result.test_name}</Text>
										<span style={{ fontSize: "11px", fontWeight: 600 }}>
											{result.result_value} {result.units}
										</span>
									</div>
								</div>
							))}
						</div>
					</SectionCard>
				)}
			</div>
		);
	};

	const renderRecommendations = () => {
		if (!analysis) return null;

		const recommendations = [
			{ key: "lifestyle_recommendations", title: "Lifestyle", icon: <HeartOutlined /> },
			{ key: "dietary_recommendations", title: "Diet", icon: <AppleOutlined /> },
			{ key: "exercise_recommendations", title: "Exercise", icon: <LineChartOutlined /> },
			{ key: "supplement_recommendations", title: "Supplements", icon: <SafetyCertificateOutlined /> },
		];

		const hasRecommendations = recommendations.some((rec) => analysis[rec.key as keyof BloodTestAnalysis]);

		if (!hasRecommendations) return null;

		return (
			<div style={{ marginBottom: "16px" }}>
				{recommendations.map((rec) => {
					const content = analysis[rec.key as keyof BloodTestAnalysis] as string;
					if (!content) return null;

					return (
						<div key={rec.key} style={{ marginBottom: "16px" }}>
							<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
								<span style={{ fontSize: 13 }}>{rec.icon}</span>
								<Text style={{ margin: 0, fontWeight: 600, fontSize: 12, display: "block" }}>{rec.title}</Text>
							</div>
							<div style={{ fontSize: "11px", lineHeight: "1.6" }}>{content}</div>
						</div>
					);
				})}
			</div>
		);
	};

	const renderHealthAnalysis = () => {
		if (!analysis) return null;

		const hasAnalysis = HEALTH_ANALYSIS_CATEGORIES.some((cat) => analysis[cat.key as keyof BloodTestAnalysis]);

		if (!hasAnalysis) return null;

		return (
			<div style={{ marginBottom: "16px" }}>
				{HEALTH_ANALYSIS_CATEGORIES.map((category) => {
					const content = analysis[category.key as keyof BloodTestAnalysis] as string;
					if (!content) return null;

					return (
						<div key={category.key} style={{ marginBottom: "16px" }}>
							<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
								<span style={{ fontSize: 13 }}><BarChartOutlined /></span>
								<Text style={{ margin: 0, fontWeight: 600, fontSize: 12, display: "block" }}>{category.title}</Text>
							</div>
							<div style={{ fontSize: "11px", lineHeight: "1.6" }}>{content}</div>
						</div>
					);
				})}
			</div>
		);
	};

	const renderFollowUp = () => {
		if (!analysis) return null;

		const hasFollowUp = analysis.follow_up_recommendations || analysis.tests_to_monitor?.length || analysis.symptoms_to_watch?.length || analysis.doctor_consultation_priority;

		if (!hasFollowUp) return null;

		return (
			<div style={{ marginBottom: "16px" }}>
				{analysis.doctor_consultation_priority && (
					<div style={{ marginBottom: "16px" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
							<UserOutlined style={{ fontSize: 13 }} />
							<Text style={{ margin: 0, fontWeight: 600, fontSize: 12, display: "block" }}>Doctor Consultation</Text>
						</div>
						<StatusTag status={getPriorityStatus(analysis.doctor_consultation_priority)} label={`${analysis.doctor_consultation_priority} Priority`} size="small" />
					</div>
				)}

				{analysis.follow_up_recommendations && (
					<div style={{ marginBottom: "16px" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
							<ClockCircleOutlined style={{ fontSize: 13 }} />
							<Text style={{ margin: 0, fontWeight: 600, fontSize: 12, display: "block" }}>Recommendations</Text>
						</div>
							<div style={{ padding: 12, borderRadius: 4, border: "1px solid var(--mg-border)" }}>
							<div style={{ fontSize: "11px", lineHeight: "1.6" }}>{analysis.follow_up_recommendations}</div>
						</div>
					</div>
				)}

				{analysis.tests_to_monitor && analysis.tests_to_monitor.length > 0 && (
					<div style={{ marginBottom: "16px" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
							<EyeOutlined style={{ fontSize: 13 }} />
							<Text style={{ margin: 0, fontWeight: 600, fontSize: 12, display: "block" }}>Tests to Monitor</Text>
						</div>
						<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
							{analysis.tests_to_monitor.map((test, index) => (
								<Tag key={index} style={{ fontSize: "11px" }}>
									{test}
								</Tag>
							))}
						</div>
					</div>
				)}

				{analysis.symptoms_to_watch && analysis.symptoms_to_watch.length > 0 && (
					<div style={{ marginBottom: "16px" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
							<SearchOutlined style={{ fontSize: 13 }} />
							<Text style={{ margin: 0, fontWeight: 600, fontSize: 12, display: "block" }}>Symptoms to Watch</Text>
						</div>
						<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
							{analysis.symptoms_to_watch.map((symptom, index) => (
								<Tag key={index} color="warning" style={{ fontSize: "11px" }}>
									{symptom}
								</Tag>
							))}
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderAbnormalResults = () => {
		if (!analysis) return null;

		const hasAbnormal = analysis.abnormal_results?.length || analysis.critical_values?.length;
		if (!hasAbnormal) return null;

		return (
			<div style={{ marginBottom: "16px" }}>
				{analysis.critical_values && analysis.critical_values.length > 0 && (
					<div style={{ marginBottom: "16px" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
							<ExclamationCircleOutlined style={{ fontSize: 13 }} />
							<Text style={{ margin: 0, fontWeight: 600, fontSize: 12 }}>Critical Values</Text>
						</div>
						<div style={{ padding: "8px 12px", borderRadius: 4, background: "var(--mg-danger-bg)", border: "1px solid var(--mg-danger)", fontSize: 11 }}>
						{analysis.critical_values.map((item, index) => (
							<div key={index} style={{ marginBottom: index < analysis.critical_values!.length - 1 ? 4 : 0, fontWeight: 500 }}>
								{item}
							</div>
						))}
					</div>
					</div>
				)}

				{analysis.abnormal_results && analysis.abnormal_results.length > 0 && (
					<div style={{ marginBottom: "16px" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
							<ExclamationCircleOutlined style={{ fontSize: 13 }} />
							<Text style={{ margin: 0, fontWeight: 600, fontSize: 12 }}>Outside reference range</Text>
						</div>
						<div style={{ padding: "8px 12px", borderRadius: 4, background: "var(--mg-warning-bg)", border: "1px solid var(--mg-warning)", fontSize: 11 }}>
						{analysis.abnormal_results.map((item, index) => (
							<div key={index} style={{ marginBottom: index < analysis.abnormal_results!.length - 1 ? 4 : 0 }}>
								{item}
							</div>
						))}
					</div>
					</div>
				)}
			</div>
		);
	};

	const renderPatientEducation = () => {
		if (!analysis) return null;

		const hasEducation = analysis.patient_education || analysis.relevant_conditions?.length || analysis.preventive_measures;

		if (!hasEducation) return null;

		return (
			<div style={{ marginBottom: "16px" }}>
				{analysis.patient_education && (
					<div style={{ marginBottom: "16px" }}>
						<div style={{ padding: 12, borderRadius: 4, border: "1px solid var(--mg-border)" }}>
							<div style={{ fontSize: 11, lineHeight: 1.6 }}>{analysis.patient_education}</div>
						</div>
					</div>
				)}

				{analysis.relevant_conditions && analysis.relevant_conditions.length > 0 && (
					<div style={{ marginBottom: "16px" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
							<InfoCircleOutlined style={{ fontSize: 13 }} />
							<Text style={{ margin: 0, fontWeight: 600, fontSize: 12 }}>Relevant Conditions</Text>
						</div>
						<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
							{analysis.relevant_conditions.map((condition, index) => (
								<Tag key={index} style={{ fontSize: "11px" }}>
									{condition}
								</Tag>
							))}
						</div>
					</div>
				)}

				{analysis.preventive_measures && (
					<div style={{ marginBottom: "16px" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
							<SafetyCertificateOutlined style={{ fontSize: 13 }} />
							<Text style={{ margin: 0, fontWeight: 600, fontSize: 12 }}>Preventive Measures</Text>
						</div>
						<div style={{ padding: 12, borderRadius: 4, border: "1px solid var(--mg-border)" }}>
							<div style={{ fontSize: 11, lineHeight: 1.6 }}>{analysis.preventive_measures}</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	// A clicked result always shows its detail on the right, even when the test has
	// no AI analysis — so this check must come before the no-analysis fallback.
	if (selectedResult) {
		return (
			<div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
				<div style={{ padding: 12, borderBottom: "1px solid var(--mg-border)" }}>{renderSelectedResultDetails()}</div>
			</div>
		);
	}

	if (!analysis || analysis.status !== "completed") {
		return renderNoAnalysis();
	}

	const outOfRangeResultsCount = bloodTest?.results?.filter((r) => calculateResultSeverity(r, rangeContext).flag !== null).length || 0;

	const items = [
		{
			key: "1",
			label: (
				<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<ExclamationCircleOutlined />
					<span style={{ fontWeight: 600, fontSize: 12 }}>Outside reference range</span>
					{outOfRangeResultsCount > 0 && <Badge count={outOfRangeResultsCount} />}
				</div>
			),
			children: renderAbnormalResults(),
		},
		{
			key: "2",
			label: (
				<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<BulbOutlined />
					<span style={{ fontWeight: 600, fontSize: 12 }}>Recommendations</span>
				</div>
			),
			children: renderRecommendations(),
		},
		{
			key: "3",
			label: (
				<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<BarChartOutlined />
					<span style={{ fontWeight: 600, fontSize: 12 }}>Health Analysis</span>
				</div>
			),
			children: renderHealthAnalysis(),
		},
		{
			key: "4",
			label: (
				<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<CalendarOutlined />
					<span style={{ fontWeight: 600, fontSize: 12 }}>Follow-up Care</span>
				</div>
			),
			children: renderFollowUp(),
		},
		{
			key: "5",
			label: (
				<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<BookOutlined />
					<span style={{ fontWeight: 600, fontSize: 12 }}>Patient Education</span>
				</div>
			),
			children: renderPatientEducation(),
		},
	].filter((item) => item.children);

	return (
		<div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<div style={{ flex: 1, overflowY: "auto", padding: "12px" }}>
				<Collapse activeKey={activeKeys} onChange={(keys) => setActiveKeys(keys as string[])} items={items} bordered={false} />
			</div>
		</div>
	);
};

export default BloodAnalysisSidebar;
